import { Component, h, Event, EventEmitter, State, Listen, Host } from '@stencil/core';
import { httpsCallable } from 'firebase/functions';
import { FirebaseService } from '../../utils/firebase';

import { addStyleSettings } from '../../utils/custom-style';
import { selection } from '../../store/selection.store';
import { settings } from '../../store/settings.store';

import { AddOn, AvailableAddOnsDataSection, CheckoutGetAvailableAddOnsRequest, CheckoutGetAvailableAddOnsResponse, OrderAddOn } from '@artask/tambr-models/models/add-on.model';



@Component({
  tag: 'tambr-checkout-add-ons-select',
  styleUrl: 'tambr-checkout-add-ons-select.css',
  shadow: true
})
export class TambrCheckoutAddOnsSelect {

  @State() availableAddOnsResponse: CheckoutGetAvailableAddOnsResponse;
  @State() addOnsLoaded: boolean = false;
  @State() addOnsLoading: boolean = true;
  @State() error: string | null = null;

  @Event() tambrCheckoutAddOnsDialogClose: EventEmitter;
  @Event() tambrCheckoutAddOnsDialogError: EventEmitter;

  @State() selectedAddOnsState: OrderAddOn[] = [];

  @Listen('click') onClickEvent( ev: MouseEvent ) {
    const targets = ev.composedPath() as EventTarget[];
    if (targets[0] instanceof Element && targets[0].id === 'tambr-add-ons-select-dialog') {
      return this.close();
    }
  }

  componentWillLoad() { this.selectedAddOnsState = selection.addOns; }

  componentDidLoad() { this.loadAddOns() }

  async loadAddOns() {
    try {
      this.addOnsLoading = true;

      const request = {
        businessId: settings.businessId,
        date: selection.date,
        startTs: selection.increments[0],
        duration: selection.duration,
      }

      const getAvailableAddOns = httpsCallable
        <CheckoutGetAvailableAddOnsRequest, CheckoutGetAvailableAddOnsResponse>
          ( FirebaseService.functions, 'checkout_getAvailableAddOns' );

      const response = await getAvailableAddOns( request );
      this.availableAddOnsResponse = response.data;
      this.addOnsLoaded = true;
      this.addOnsLoading = false;
      console.log('loadAddOns data', response.data);

    } catch(e) { 
      console.error(e); 
      this.tambrCheckoutAddOnsDialogError.emit();
    }    
  }
  
  getSelectedAddOn( addOn: AddOn ) : OrderAddOn { 
    return this.selectedAddOnsState.find( selectedAddOn => selectedAddOn.id === addOn.id ); 
  }

  checkboxClass( addOn: AddOn ) { return this.getSelectedAddOn( addOn ) ? 'checkbox-on' : 'checkbox-off'; }

  toggleAddOn ( addOn: AddOn ) { 
    if ( addOn.availableQuantity !== 0 ) {
      if ( this.getSelectedAddOn(addOn) ) {
        this.selectedAddOnsState = this.selectedAddOnsState.filter( selectedAddOn => selectedAddOn.id !== addOn.id );
      } else {
        const OrderAddOn: OrderAddOn = {
          id: addOn.id,
          title: addOn.title,
          description: addOn.description,
          categoryId: addOn.categoryId,
          quantity: 1,
          type: addOn.type,
          price: addOn.price,
          priceScope: addOn.priceScope
        }
        this.selectedAddOnsState = [ ...this.selectedAddOnsState, OrderAddOn ];
      }
    }   
  }

  addOnQuantityChange( addOn: AddOn, newQuantity: number ) {
    this.getSelectedAddOn( addOn ).quantity = newQuantity;
  }

  close() { this.tambrCheckoutAddOnsDialogClose.emit(); }

  save() { 
    selection.addOns = this.selectedAddOnsState;
    this.close();
  }


  render() {
    return (
      <Host style={ addStyleSettings() }>
        <div id="tambr-add-ons-select-dialog" class="tambr-overlay">
          <div class='tambr-modal-window add-on-selection-window'>

            <div class="tambr-modal-header">
              <tambr-icon type="close" class="close-button" onClick={ () => this.close() } />
            </div>

            { this.addOnsLoading
              ? <div class="add-ons-loading-spinner"><tambr-loading-spinner /></div>
              : [
                  <div class="tambr-modal-body with-padding">{ this.renderAddOns() }</div>,      
                  <div class="tambr-action-buttons">
                    <button onClick={ () => this.save() } 
                      disabled={ this.addOnsLoading } 
                      class={ 'tambr-button' + (this.addOnsLoading ? ' loading' : '') }>
                        <div class="tambr-button-loading"><div>Loading...</div></div>
                        <span>Add Selected</span>
                    </button>
                  </div>
                ]
            }

          </div>
        </div>
      </Host>
    );
  }

  renderAddOns() {
    if ( this.availableAddOnsResponse?.data?.length ) {
      return this.availableAddOnsResponse.data.map( 
        (section: AvailableAddOnsDataSection) => this.renderAddOnSection( section )
      );      
    } else {
      return (<div>There are no available add-ons.</div>);
    }    
  }

  renderAddOnSection( section: AvailableAddOnsDataSection ) {
    return (
      <div class="add-on-section">
        { section.title && (<div class="add-on-section-title">{ section.title }</div>) }
        <div class="add-on-section-addons">
          { section.addOns.map( (addOn: AddOn) => this.renderAddOn(addOn) ) }
        </div>
      </div>
    );
  }

  renderAddOn( addOn: AddOn ) {
    return (
      <div class={"add-on-item" + (addOn.availableQuantity === 0 ? ' disabled' : '')} >
        <div class="add-on-row">
          <div class="add-on-checkbox-label">
            <div class="add-on-checkbox-col" onClick={ () => this.toggleAddOn(addOn) }>
              <tambr-icon type={ this.checkboxClass( addOn ) }  />
            </div>  
            <div class="add-on-info-col" onClick={ () => this.toggleAddOn(addOn) }>
              <div class="add-on-title">
                <span>{ addOn.title }</span>
                { ( addOn.availableQuantity === 0 ) &&
                  <div class='add-on-unavailable-chip'>{ ( addOn.type === 'consumable' ) ? 'Sold out': 'Unavailable' }</div>
                }
              </div>
            </div>
          </div>
          <div class="add-on-quantity-price-col">
            {
              this.getSelectedAddOn(addOn) && addOn.allowMultiple ? 
                (<div class="add-on-quantity-col">
                  <tambr-quantity-selector 
                    previousQuantity={ this.getSelectedAddOn(addOn)?.quantity } 
                    maxQuantity={ addOn.availableQuantity } 
                    onQuantityChange={ (event) => this.addOnQuantityChange(addOn, event.detail ) }  />
                </div>) : ''
            }
            <div class="add-on-price-col">{ this.renderAddOnPrice( addOn ) }</div>
          </div>
        </div>
        <div class="add-on-description">{ addOn.description }</div>

      </div> 
    );
  }

  renderAddOnPrice( addOn: AddOn ) {
    if ( addOn.price <= 0 ) return (<div class="add-on-price-free">Free</div>);
    const output = [ <div class="add-on-price">${ (addOn.price/100).toFixed(2) }</div> ];
    if ( addOn.type !== 'consumable' ) {
      const priceScope = addOn.priceScope === 'slot' ? 'time slot' : 'session';
      output.push(<div class="add-on-price-scope">per { priceScope }</div>);      
    }
    return output;
  }

}
